import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  ListItemText,
  MenuItem,
  TextField,
} from '@mui/material';
import { EditorOption } from './projectListModel';

// Standard dialogs: white, the theme's radius and shadow, a plain title, the
// confirming button on the right. No tinted title bands or see-through paper.

interface CancelProjectDialogProps {
  open: boolean;
  projectName?: string;
  cancelling: boolean;
  error?: string | null;
  onConfirm: () => void;
  onClose: () => void;
}

export const CancelProjectDialog: React.FC<CancelProjectDialogProps> = ({
  open,
  projectName,
  cancelling,
  error,
  onConfirm,
  onClose,
}) => (
  <Dialog
    open={open}
    onClose={() => { if (!cancelling) onClose(); }}
    maxWidth="xs"
    fullWidth
    aria-labelledby="cancel-project-title"
    aria-describedby="cancel-project-description"
  >
    <DialogTitle id="cancel-project-title">Cancel this project?</DialogTitle>
    <DialogContent>
      <DialogContentText id="cancel-project-description">
        {projectName ? `“${projectName}”` : 'This project'} will stop, and if an editor is working on it they'll be told.
        It stays in your list as cancelled, and a cancelled project can't be reopened.
      </DialogContentText>
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
    </DialogContent>
    <DialogActions sx={{ px: 3, pb: 2 }}>
      <Button onClick={onClose} disabled={cancelling}>
        Keep project
      </Button>
      <Button onClick={onConfirm} disabled={cancelling} variant="contained" color="error">
        {cancelling ? 'Cancelling…' : 'Cancel project'}
      </Button>
    </DialogActions>
  </Dialog>
);

interface AssignEditorDialogProps {
  open: boolean;
  projectName?: string;
  editors: EditorOption[];
  editorId: string;
  onEditorChange: (editorId: string) => void;
  note: string;
  onNoteChange: (note: string) => void;
  assigning: boolean;
  error?: string | null;
  onConfirm: () => void;
  onClose: () => void;
}

export const AssignEditorDialog: React.FC<AssignEditorDialogProps> = ({
  open,
  projectName,
  editors,
  editorId,
  onEditorChange,
  note,
  onNoteChange,
  assigning,
  error,
  onConfirm,
  onClose,
}) => {
  const editorName = (id: string) => {
    const editor = editors.find((e) => e.id === id);
    return editor ? editor.name || editor.email : '';
  };
  return (
    <Dialog
      open={open}
      onClose={() => { if (!assigning) onClose(); }}
      maxWidth="sm"
      fullWidth
      aria-labelledby="assign-editor-title"
    >
      <DialogTitle id="assign-editor-title">Assign an editor</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2.5 }}>
          Choose who will edit {projectName ? `“${projectName}”` : 'this project'}. We'll email them the project.
        </DialogContentText>

        {editors.length === 0 ? (
          <Alert
            severity="info"
            action={
              <Button color="inherit" size="small" component={RouterLink} to="/my-editors">
                Go to editors
              </Button>
            }
          >
            You don't have any active editors yet. Invite one first.
          </Alert>
        ) : (
          <TextField
            select
            fullWidth
            label="Editor"
            value={editorId}
            onChange={(e) => onEditorChange(e.target.value)}
            SelectProps={{ renderValue: (value) => editorName(String(value)) }}
          >
            {editors.map((editor) => (
              <MenuItem key={editor.id} value={editor.id}>
                <ListItemText primary={editor.name || editor.email} secondary={editor.name ? editor.email : undefined} />
              </MenuItem>
            ))}
          </TextField>
        )}

        <TextField
          label="Note to the editor (optional)"
          multiline
          rows={4}
          fullWidth
          value={note}
          onChange={(e) => onNoteChange(e.target.value)}
          inputProps={{ maxLength: 2000 }}
          placeholder="Anything they should know before they start"
          helperText="Included in the assignment email we send them, and saved on the project."
          sx={{ mt: 2.5 }}
        />

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={assigning}>
          Cancel
        </Button>
        <Button onClick={onConfirm} variant="contained" disabled={!editorId || assigning}>
          {assigning ? 'Assigning…' : 'Assign editor'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface MessageEditorDialogProps {
  open: boolean;
  projectName?: string;
  editorName?: string | null;
  editorEmail?: string | null;
  message: string;
  onMessageChange: (message: string) => void;
  sending: boolean;
  error?: string | null;
  onSend: () => void;
  onClose: () => void;
}

export const MessageEditorDialog: React.FC<MessageEditorDialogProps> = ({
  open,
  projectName,
  editorName,
  editorEmail,
  message,
  onMessageChange,
  sending,
  error,
  onSend,
  onClose,
}) => (
  <Dialog
    open={open}
    onClose={() => { if (!sending) onClose(); }}
    maxWidth="sm"
    fullWidth
    aria-labelledby="message-editor-title"
  >
    <DialogTitle id="message-editor-title">Message editor</DialogTitle>
    <DialogContent>
      <DialogContentText sx={{ mb: 2 }}>
        To {editorName || 'your editor'}{editorEmail ? ` (${editorEmail})` : ''}
        {projectName ? `, about “${projectName}”` : ''}.
      </DialogContentText>
      <TextField
        autoFocus
        label="Message"
        multiline
        rows={6}
        fullWidth
        value={message}
        onChange={(e) => onMessageChange(e.target.value)}
        helperText="Saved on the project and emailed to the editor."
        placeholder={projectName ? `Your message about “${projectName}”` : 'Your message'}
      />
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
    </DialogContent>
    <DialogActions sx={{ px: 3, pb: 2 }}>
      <Button onClick={onClose} disabled={sending}>
        Cancel
      </Button>
      <Button onClick={onSend} variant="contained" disabled={!message.trim() || sending}>
        {sending ? 'Sending…' : 'Send message'}
      </Button>
    </DialogActions>
  </Dialog>
);
